import { DataStatus, LOADING, OK } from "helpers/data-statuses"
import { Period, SAME_DAY } from "helpers/dates"
import { getToday, IsoDate } from "helpers/types/dates"
import { isEmpty } from "lodash"
import moment from "moment"

/**
 * Calculates the effective end date, preferring provided endDate over boundary latest
 */
export function calculateEffectiveEndDate(endDate: IsoDate | null | undefined, boundaryLatestDate: IsoDate) {
  return endDate || boundaryLatestDate
}

/**
 * Determines if the end date is outside the valid boundary range
 */
export function isEndDateOutsideBoundary(effectiveEndDate: IsoDate | null, boundaryLatestDate: IsoDate, boundaryEarliestDate: IsoDate) {
  return !effectiveEndDate
         || effectiveEndDate > boundaryLatestDate
         || effectiveEndDate < boundaryEarliestDate
}

/**
 * Calculates the default end date when none is provided
 */
export function calculateDefaultEndDate(isIntradayEnabled: boolean, isIntradayBatchAvailable: boolean, today: IsoDate, boundaryLatestDate: IsoDate) {
  return isIntradayEnabled ? today : boundaryLatestDate
}

/**
 * Determines if we should switch to today's date for initial intraday load
 */
export function shouldUseInitialIntradayDate(initialEndDate: IsoDate | null, endDate: IsoDate | null, boundaryLatestDate: IsoDate, isIntradayMode: boolean) {
  return !initialEndDate && endDate === boundaryLatestDate && isIntradayMode
}

/**
 * Determines if we should correct the end date to boundary latest due to boundary violations
 */
export function shouldCorrectBoundaryDate(isIntradayEnabled: boolean, endDateOutsideBoundary: boolean) {
  return !isIntradayEnabled && endDateOutsideBoundary
}

/**
 * Determines if we should use today's date as an override
 */
export function shouldUseTodayOverride(isIntradayMode: boolean, initialEndDate: IsoDate | null, today: IsoDate) {
  return isIntradayMode && (initialEndDate === today)
}

/**
 * Determines if we should rollover to the latest available date
 */
export function shouldRolloverToLatest(useLatestDate: boolean, effectiveEndDate: IsoDate, boundaryLatestDate: IsoDate, isEndDateToday: boolean) {
  return useLatestDate && effectiveEndDate !== boundaryLatestDate && !isEndDateToday
}

/**
 * Boundary violations are clamped to boundaryLatestDate, except when in intraday mode and endDate is today.
 */
export function calculatePerformanceEndDate(
  isIntradayMode: boolean,
  isEndDateToday: boolean,
  endDateOutsideBoundary: boolean,
  boundaryLatestDate: IsoDate,
  effectiveEndDate: IsoDate,
) {
  if (isIntradayMode && isEndDateToday) {
    return effectiveEndDate
  }
  return endDateOutsideBoundary ? boundaryLatestDate : effectiveEndDate
}

/**
 * Determines if we need a default end date (when none is provided)
 */
export function needsDefaultEndDate(endDate: IsoDate | null | undefined, boundaryLatestDate: IsoDate | null) {
  return !endDate && !!boundaryLatestDate
}

/**
 * Checks if the effective end date is today
 */
export function isEffectiveEndDateToday(effectiveEndDate: IsoDate, today: IsoDate) {
  return effectiveEndDate === today
}

/**
 * Determines if intraday batch is in loading state
 */
export function isIntradayBatchLoading(isIntradayEnabled: boolean, intradayBatchStatus: DataStatus) {
  return isIntradayEnabled && intradayBatchStatus === LOADING
}

/**
 * Creates a period update object with consistent structure
 */
export function createPeriodUpdate(period: Period, endDate: IsoDate, startDate: IsoDate) {
  return {
    period,
    endDate,
    startDate,
  }
}

/**
 * Returns the intradayBatchMetadata.asOf timestamp for query cache busting when:
 * - No real batch ID exists (non-intraday instrument)
 * - A timestamp is available from the websocket
 * - The period is 30 days or fewer
 * Returns undefined otherwise (batch instruments refresh via batchId, long periods skip refresh).
 */
export function getIntradayPriceRefreshTime(
  intradayBatchMetadata: { id?: string, asOf?: string } | null,
  startDate: IsoDate,
  endDate: IsoDate,
): string | undefined {
  if (intradayBatchMetadata?.id || !intradayBatchMetadata?.asOf) return undefined
  const daySpan = Math.abs(moment(endDate).diff(moment(startDate), "days"))
  return daySpan <= 30 ? intradayBatchMetadata.asOf : undefined
}

/**
 * Comprehensive date adjustment calculation that returns all computed values
 * This function orchestrates all the individual calculations
 */
export function calculateDateAdjustments({
  endDate,
  boundaryLatestDate,
  boundaryEarliestDate,
  isIntradayMode,
  initialEndDate,
  useLatestDate,
  period,
  startDate,
  isIntradayEnabled,
  intradayBatchStatus,
  lastIntradayBatch,
  isIntradayAnalyticsEnabled = false,
}: {
  endDate: IsoDate | null
  boundaryLatestDate: IsoDate
  boundaryEarliestDate: IsoDate
  isIntradayMode: boolean
  initialEndDate: IsoDate | null
  useLatestDate: boolean
  period: Period
  startDate: IsoDate
  isIntradayEnabled: boolean
  intradayBatchStatus: DataStatus
  lastIntradayBatch: { id: string }
  isIntradayAnalyticsEnabled?: boolean
}): {
  isLoading: boolean
  effectiveEndDate: IsoDate
  performanceEndDate: IsoDate
  finalPeriodState: {
    period: Period
    endDate: IsoDate | null
    startDate: IsoDate
  }
  hasChanges: boolean
  appliedAdjustments: { type: string }[]
  computedValues: {
    effectiveEndDate: IsoDate
    isEndDateToday: boolean
    endDateOutsideBoundary: boolean
    isLoadingIntradayBatch: boolean
  }
} {
  const today = getToday()
  const isEffectivelyIntraday = isIntradayEnabled || isIntradayAnalyticsEnabled
  const effectiveEndDate = calculateEffectiveEndDate(endDate, isEffectivelyIntraday ? today : boundaryLatestDate)
  const endDateOutsideBoundary = isEndDateOutsideBoundary(effectiveEndDate, boundaryLatestDate, boundaryEarliestDate)
  const isEndDateToday = isEffectiveEndDateToday(effectiveEndDate, today)
  const isLoadingIntradayBatch = isIntradayBatchLoading(isIntradayEnabled, intradayBatchStatus)

  if (isLoadingIntradayBatch) {
    return {
      isLoading: true,
      effectiveEndDate,
      performanceEndDate: effectiveEndDate,
      finalPeriodState: { period, endDate, startDate },
      hasChanges: false,
      appliedAdjustments: [],
      computedValues: {
        effectiveEndDate,
        isEndDateToday,
        endDateOutsideBoundary,
        isLoadingIntradayBatch: true, // This is for consuming components to optionally show loading indicators for intraday-specific features
      },
    }
  }

  const periodUpdates = []

  const performanceEndDate = calculatePerformanceEndDate(
    isIntradayMode,
    isEndDateToday,
    endDateOutsideBoundary,
    boundaryLatestDate,
    effectiveEndDate,
  )

  // Check for various period adjustments needed. Order matters!

  // Basic data-driven adjustments
  if (needsDefaultEndDate(endDate, boundaryLatestDate)) {
    const isIntradayBatchAvailable = !isEmpty(lastIntradayBatch) && intradayBatchStatus === OK
    const defaultEndDate = calculateDefaultEndDate(isEffectivelyIntraday, isIntradayBatchAvailable, today, boundaryLatestDate)
    // When intraday analytics is enabled, default to today view (start=today, end=today)
    const isDefaultingToToday = isIntradayAnalyticsEnabled && defaultEndDate === today
    const defaultStartDate = isDefaultingToToday ? today : startDate
    const defaultPeriod = isDefaultingToToday ? SAME_DAY : period
    periodUpdates.push({
      type: "DEFAULT_END_DATE",
      update: createPeriodUpdate(defaultPeriod, defaultEndDate, defaultStartDate),
    })
  } else if (shouldCorrectBoundaryDate(isEffectivelyIntraday, endDateOutsideBoundary)) {
    // End date outside boundary and not in intraday mode
    periodUpdates.push({
      type: "BOUNDARY_CORRECTION",
      update: { endDate: boundaryLatestDate }, // Only change endDate, preserve startDate
    })
  }

  // System convenience (can override basic adjustments)
  if (shouldRolloverToLatest(useLatestDate, effectiveEndDate, boundaryLatestDate, isEndDateToday)) {
    // useLatestDate enabled and not at boundary latest
    periodUpdates.push({
      type: "ROLLOVER_TO_LATEST",
      update: { endDate: boundaryLatestDate }, // Only change endDate, preserve startDate
    })
  }

  // Explicit user intent (wins over everything else by being last)
  if (shouldUseInitialIntradayDate(initialEndDate, endDate, boundaryLatestDate, isIntradayMode)) {
    // Initially loaded in intraday mode with boundary latest date
    periodUpdates.push({
      type: "INITIAL_INTRADAY_DATE",
      update: { endDate: today }, // Only change endDate, preserve custom startDate
    })
  } else if (shouldUseTodayOverride(isIntradayMode, initialEndDate, today)) {
    // Intraday mode with initial end date as today
    periodUpdates.push({
      type: "TODAY_OVERRIDE",
      update: { endDate: today }, // Only change endDate, preserve custom startDate
    })
  }

  const initialPeriodState = { period, endDate, startDate }
  const finalPeriodState = periodUpdates.reduce((currentState, { update }) => {
    return { ...currentState, ...update }
  }, initialPeriodState)

  // Check if any changes were made
  const hasChanges = finalPeriodState.period !== period
                  || finalPeriodState.startDate !== startDate
                  || finalPeriodState.endDate !== endDate

  return {
    isLoading: false,
    effectiveEndDate,
    performanceEndDate,
    finalPeriodState,
    hasChanges,
    // [TODO] I REALLY don't like this pattern. Test only code making it to production is not great,
    // and it makes the unit test care about the internals of the function, which they shouldn't.
    appliedAdjustments: periodUpdates.map(({ type }) => ({ type })), // For debugging and testing
    computedValues: {
      effectiveEndDate,
      isEndDateToday,
      endDateOutsideBoundary,
      isLoadingIntradayBatch: false,
    },
  }
}
